import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bulkUpdateTransactionCategory,
  createTransaction,
  fetchAccounts,
  fetchTransactions,
  fetchTransactionsInRange,
  fetchTransactionsPage,
  updateTransactionCategory,
  updateTransactionCleared,
  updateTransactionSplits,
  type NewTransactionInput,
  type SplitInput,
} from './repository'
import type { Transaction, TransactionStatus } from './types'

export function useTransactions(limitRows = 500) {
  return useQuery({
    queryKey: ['transactions', { limit: limitRows }] as const,
    queryFn: () => fetchTransactions(limitRows),
    staleTime: 30_000,
  })
}

/**
 * Cursor-paginated transactions. Returns pages of `pageSize` rows
 * newest-first; consumer calls `fetchNextPage()` to walk older.
 *
 * Why useInfiniteQuery over useQuery + manual append: TanStack
 * handles cache invalidation per-page on mutations, exposes
 * isFetchingNextPage for spinner UX, and the cache key cleanly
 * scopes "all loaded pages" so optimistic edits hit every page.
 */
export function useInfiniteTransactions(pageSize = 2000, dateFloor: string | null = null) {
  return useInfiniteQuery({
    // Floor goes in the key so switching between date-range chips
    // refetches against a smaller server-side window rather than
    // re-using cache with the old floor's rows.
    queryKey: ['transactions', 'infinite', { pageSize, dateFloor }] as const,
    queryFn: ({ pageParam }) => fetchTransactionsPage(pageParam, pageSize, dateFloor),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
  })
}

export function useTransactionsInRange(startISO: string, endISO: string, enabled = true) {
  return useQuery({
    queryKey: ['transactions', 'range', { startISO, endISO }] as const,
    queryFn: () => fetchTransactionsInRange(startISO, endISO),
    staleTime: 30_000,
    enabled,
  })
}

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'] as const,
    queryFn: fetchAccounts,
    staleTime: 60_000,
  })
}

interface RecategorizeArgs {
  transactionId: string
  categoryId: string | null
}

interface BulkRecategorizeArgs {
  ids: string[]
  categoryId: string | null
}

// Patch every cached transactions list in place. Handles both the
// flat `Transaction[]` (legacy useTransactions) and the
// `{ pages: [{ rows, nextCursor }] }` shape from useInfiniteTransactions.
function patchEveryCachedList(
  qc: ReturnType<typeof useQueryClient>,
  apply: (t: Transaction) => Transaction,
) {
  const entries = qc.getQueriesData<unknown>({ queryKey: ['transactions'] })
  for (const [key, data] of entries) {
    if (!data) continue
    if (Array.isArray(data)) {
      qc.setQueryData<Transaction[]>(key, (data as Transaction[]).map(apply))
    } else if (typeof data === 'object' && 'pages' in (data as object)) {
      const inf = data as { pages: Array<{ rows: Transaction[]; nextCursor: string | null }>; pageParams: unknown[] }
      qc.setQueryData(key, {
        ...inf,
        pages: inf.pages.map((p) => ({ ...p, rows: p.rows.map(apply) })),
      })
    }
  }
  return entries
}

export function useBulkRecategorize() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: BulkRecategorizeArgs) =>
      bulkUpdateTransactionCategory(args.ids, args.categoryId),
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: ['transactions'] })
      const idSet = new Set(args.ids)
      const previous = patchEveryCachedList(qc, (t) =>
        idSet.has(t.id) ? { ...t, categoryId: args.categoryId } : t,
      )
      return { previous }
    },
    onError: (_err, _args, ctx) => {
      if (!ctx?.previous) return
      for (const [key, data] of ctx.previous) qc.setQueryData(key, data)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

interface SetClearedArgs {
  transactionId: string
  cleared: TransactionStatus
}

export function useSetCleared() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: SetClearedArgs) =>
      updateTransactionCleared(args.transactionId, args.cleared),
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: ['transactions'] })
      const previous = patchEveryCachedList(qc, (t) =>
        t.id === args.transactionId ? { ...t, cleared: args.cleared } : t,
      )
      return { previous }
    },
    onError: (_err, _args, ctx) => {
      if (!ctx?.previous) return
      for (const [key, data] of ctx.previous) qc.setQueryData(key, data)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

interface SplitArgs {
  transaction: Transaction
  splits: SplitInput[]
}

export function useUpdateSplits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: SplitArgs) => updateTransactionSplits(args.transaction, args.splits),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

export function useCreateTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: NewTransactionInput) => createTransaction(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

export function useRecategorize() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: RecategorizeArgs) => {
      await updateTransactionCategory(args.transactionId, args.categoryId)
      return args
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: ['transactions'] })
      const previousList = patchEveryCachedList(qc, (t) =>
        t.id === args.transactionId ? { ...t, categoryId: args.categoryId } : t,
      )
      return { previousList }
    },
    onError: (_err, _args, ctx) => {
      // Roll back on error so the UI doesn't lie.
      if (!ctx?.previousList) return
      for (const [key, data] of ctx.previousList) qc.setQueryData(key, data)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}
